"use client";

/**
 * VOS DS 2.0 — SearchInput: campo de busca padrão (lupa + limpar + loading +
 * atalho opcional). Substitui as cópias locais de "lupa absoluta + input".
 * Controlado; a lógica de busca (debounce, fetch) é do consumidor.
 */
import { t } from "../lib/i18n";
import { forwardRef, type InputHTMLAttributes } from "react";
import { Search, X } from "lucide-react";
import { cn } from "../lib/cn";
import { Spinner } from "./spinner";
import { Kbd } from "./kbd";

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> {
  /** Mostra o × de limpar quando há valor. */
  onClear?: () => void;
  /** Troca a lupa por spinner (busca em andamento). */
  loading?: boolean;
  /** Dica de atalho (ex.: ["mod","K"]) — some quando há valor. */
  shortcutKeys?: string[];
  /** Lado da lupa (founder 30/07: busca local usa "right", estilo referência). */
  iconSide?: "left" | "right";
  /** Nome acessível. Obrigatório quando não houver <label> externo. */
  "aria-label"?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { onClear, loading = false, shortcutKeys, iconSide = "left", className, value, placeholder, ...props },
  ref,
) {
  const hasValue = value !== undefined && value !== null && String(value).length > 0;
  return (
    <div className={cn("relative", className)}>
      <span
        className={cn(
          "pointer-events-none absolute top-1/2 -translate-y-1/2 text-[var(--ink-3)]",
          iconSide === "right" ? "right-3" : "left-3",
        )}
        aria-hidden
      >
        {loading ? <Spinner size="sm" /> : <Search size={14} />}
      </span>
      <input
        ref={ref}
        type="text"
        value={value}
        placeholder={placeholder ?? t("Buscar…")}
        className={cn(
          /* §21 (08/08): SearchInput vive em formulário/picker — altura de CAMPO
             (38px), não de toolbar. Quem precisar de 30px numa régua §30b pode
             re-pinar --vos-field-h no wrapper. */
          "vos-field h-[var(--vos-field-h,38px)] w-full rounded-lg border border-solid border-[var(--line-2)] bg-[var(--field-bg)] text-[length:var(--fs-field,13px)] text-[var(--ink)] outline-none transition duration-vos ease-vos placeholder:text-[var(--ink-3)] hover:border-[var(--ink-3)] focus:border-[var(--accent)] focus-visible:ring-2 focus-visible:ring-[var(--accent-soft)] disabled:cursor-not-allowed disabled:opacity-50",
          iconSide === "right" ? "pl-3 pr-9" : "pl-9 pr-9",
        )}
        {...props}
      />
      {hasValue && onClear ? (
        <button
          type="button"
          aria-label={t("Limpar busca")}
          onClick={onClear}
          className="absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-[var(--ink-3)] transition-colors hover:bg-[var(--panel-3)] hover:text-[var(--ink)]"
        >
          <X size={14} aria-hidden />
        </button>
      ) : shortcutKeys?.length ? (
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2" aria-hidden>
          <Kbd keys={shortcutKeys} size="xs" />
        </span>
      ) : null}
    </div>
  );
});
